import { useState } from "react";
import { Camera, X, Check, AlertTriangle, Image as ImageIcon, Eye, Sun, FileText } from "lucide-react";

const STORAGE_KEY = "medx_scan_guide_seen";

/**
 * Returns true the FIRST time this helper is called (i.e. the guide was never
 * dismissed before). Used by ScanUploadModal to auto-open the guide for new
 * patients.
 */
export function shouldShowScanGuide() {
    try { return !localStorage.getItem(STORAGE_KEY); } catch { return false; }
}

/**
 * ScanPhotoGuideModal — one-time tutorial shown before the first scan upload.
 * Explains how to photograph a Medx AI slip so Gemini OCR can read every
 * metric: fill the frame, no glare, no other objects in the shot, keep text
 * sharp and legible. Patients can also re-open it anytime via "Photo tips" in
 * the upload modal.
 */
export function ScanPhotoGuideModal({ onClose, onGotIt }) {
    const dismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch {/* ignore */}
        onGotIt?.();
    };
    return (
        <div
            className="fixed inset-0 z-[60] grid place-items-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            data-testid="scan-photo-guide-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
                style={{ maxHeight: "90vh" }}
            >
                {/* Header — pinned */}
                <div className="relative p-6 bg-gradient-to-br from-[var(--medx-red)]/8 to-[var(--medx-orange)]/15 border-b border-orange-100 shrink-0">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-white/60 grid place-items-center text-gray-500"
                        aria-label="Close"
                        data-testid="scan-guide-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white shadow-lg">
                            <Camera className="w-6 h-6" />
                        </div>
                        <div>
                            <div className="text-[10px] font-mono tracking-[0.22em] text-[var(--medx-red)]">BEFORE YOU UPLOAD</div>
                            <h3 className="font-display text-xl font-semibold">How to photograph your slip</h3>
                        </div>
                    </div>
                    <p className="text-sm text-gray-700 mt-3 leading-relaxed">
                        Our AI reads <b>30+ metrics</b> directly from the paper slip. The clearer the photo, the faster
                        your dashboard updates. Please follow these rules — unclear or wrong images will be rejected.
                    </p>
                </div>

                {/* Scrollable body — this is the only part that scrolls */}
                <div
                    className="flex-1 min-h-0 overflow-y-auto p-6 space-y-3"
                    data-testid="scan-guide-scroll-body"
                >
                    <div className="text-[11px] font-mono tracking-[0.22em] text-emerald-700">DO</div>
                    <Tip
                        tone="good"
                        icon={FileText}
                        title="Only the slip in frame"
                        body="The picture must contain just the Medx AI assessment slip — no other elements, fingers, phones, cups, pens, or other papers in the shot."
                    />
                    <Tip
                        tone="good"
                        icon={Eye}
                        title="Clear and easy to read"
                        body="Every line of printed text must be sharp and legible. If you can't comfortably read it on your screen, our AI can't either."
                    />
                    <Tip
                        tone="good"
                        icon={Sun}
                        title="Even, bright light"
                        body="Lay the slip flat on a plain surface, use natural daylight if possible. Avoid shadows and harsh angled light."
                    />
                    <Tip
                        tone="good"
                        icon={ImageIcon}
                        title="Fill the frame"
                        body="Hold your phone directly above the slip so it fills most of the picture. Keep edges straight — don't tilt the camera."
                    />

                    <div className="text-[11px] font-mono tracking-[0.22em] text-red-700 pt-2">DON'T</div>
                    <Tip
                        tone="bad"
                        icon={AlertTriangle}
                        title="No foggy or blurry photos"
                        body="If the slip looks foggy, out of focus, smudged, or blurred in any way, retake it. Blurry pictures are rejected automatically."
                    />
                    <Tip
                        tone="bad"
                        icon={AlertTriangle}
                        title="No glare, shadows or crumples"
                        body="Avoid camera flash reflecting off the paper, dark shadows cutting across the text, or wrinkled slips. Flatten the paper first."
                    />
                    <Tip
                        tone="bad"
                        icon={AlertTriangle}
                        title="No non-Medx documents"
                        body="Only authentic Medx AI assessment slips are accepted. Photos of unrelated receipts, screenshots or hand-written notes will be rejected."
                    />
                </div>

                {/* Footer — pinned so the Continue button is ALWAYS on screen */}
                <div className="p-4 border-t border-gray-100 bg-white flex items-center justify-between gap-3 shrink-0">
                    <div className="text-[11px] text-gray-500 leading-tight hidden sm:block">
                        Tip: tap the slip on screen before taking the photo so your camera focuses.
                    </div>
                    <button
                        onClick={dismiss}
                        className="btn-primary inline-flex items-center justify-center gap-2 shrink-0 w-full sm:w-auto"
                        data-testid="scan-guide-got-it"
                    >
                        <Check className="w-4 h-4" /> Got it — continue
                    </button>
                </div>
            </div>
        </div>
    );
}

function Tip({ tone, icon: Icon, title, body }) {
    const good = tone === "good";
    return (
        <div className="flex items-start gap-3">
            <div
                className={`w-8 h-8 rounded-full grid place-items-center shrink-0 mt-0.5 ${
                    good ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
                }`}
            >
                {good ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
            </div>
            <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">{title}</div>
                <div className="text-xs text-gray-600 leading-relaxed mt-0.5">{body}</div>
            </div>
        </div>
    );
}
